"use client";

import { useState, type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { AuthEntryPoint } from "@/lib/analytics/types";
import type { SignedIn } from "@/lib/auth/sign-in/result";
import type { SignInModalPurpose, SignInModalView } from "@/lib/auth/sign-in-modal-store";
import type { StatedAccountUses } from "@shared/auth/account-audience";
import { AboutYouStep, type AboutYouResult } from "./steps/about-you-step";
import { ConfirmEmailStep } from "./steps/confirm-email-step";
import { EmailCodeStep } from "./steps/email-code-step";
import { LogInStep } from "./steps/log-in-step";
import { StartStep } from "./steps/start-step";

export type AuthStep = SignInModalView;

export interface AuthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entryPoint: AuthEntryPoint;
  initialStep?: AuthStep;
  purpose?: SignInModalPurpose;
  reason?: string;
  notice?: string;
  initialEmail?: string;
  initialCode?: string;
  intent?: StatedAccountUses;
  quoteClaimToken?: string;
  showProviders?: boolean;
  onSignedIn: (result: SignedIn, about?: AboutYouResult) => void | Promise<void>;
}

const START_TITLES: Record<SignInModalPurpose, string> = {
  "sign-up": "Create your Repackify account",
  "log-in": "Log in to Repackify",
};

const TITLES: Record<AuthStep, string> = {
  start: "Log in or create your account",
  "log-in": "Log in with a password",
  code: "Check your email",
  verify: "Check your email",
  about: "Welcome to Repackify",
};

function useAuthSteps(props: AuthDialogProps) {
  const [step, setStep] = useState<AuthStep>(props.initialStep ?? "start");
  const [email, setEmail] = useState(props.initialEmail ?? "");
  const [codeSent, setCodeSent] = useState(false);
  const [signedIn, setSignedIn] = useState<SignedIn | null>(null);
  const [purpose, setPurpose] = useState(props.purpose);
  const onSignedIn = async (result: SignedIn) => {
    if (result.isNew) {
      setSignedIn(result);
      setStep("about");
      return;
    }
    await props.onSignedIn(result);
  };
  const finishAbout = (about: AboutYouResult) => {
    void props.onSignedIn(signedIn ?? { status: "signed_in", isNew: true }, about);
  };
  const switchTo = (next: AuthStep) => (currentEmail: string) => {
    setEmail(currentEmail);
    setCodeSent(false);
    setStep(next);
  };
  const codeWasSent = (sentTo: string) => {
    setEmail(sentTo);
    setCodeSent(true);
    setStep("code");
  };
  return { step, setStep, email, codeSent, onSignedIn, finishAbout, switchTo, codeWasSent, purpose, setPurpose };
}

function StepBody(props: AuthDialogProps & { steps: ReturnType<typeof useAuthSteps> }) {
  const { steps } = props;
  const shared = { entryPoint: props.entryPoint, onSignedIn: steps.onSignedIn };
  const toVerify = () => steps.setStep("verify");
  const toStart = () => steps.setStep("start");
  const showProviders = props.showProviders === true;
  const bodies: Record<AuthStep, () => ReactNode> = {
    start: () => (
      <StartStep entryPoint={props.entryPoint} initialEmail={steps.email} notice={props.notice} showProviders={showProviders}
        onCodeSent={steps.codeWasSent} onUsePassword={steps.purpose === "sign-up" ? undefined : steps.switchTo("log-in")}
        onLogIn={steps.purpose === "sign-up" ? () => steps.setPurpose("log-in") : undefined} />
    ),
    "log-in": () => (
      <LogInStep {...shared} initialEmail={steps.email} quoteClaimToken={props.quoteClaimToken} notice={props.notice} showProviders={false}
        onNeedsCode={toVerify} onUseCode={steps.switchTo("start")} onSignUp={steps.switchTo("start")} />
    ),
    code: () => (
      <EmailCodeStep {...shared} initialEmail={steps.email} initialCode={props.initialCode} codeSent={steps.codeSent} quoteClaimToken={props.quoteClaimToken}
        onNeedsCode={toVerify} onBack={steps.switchTo("start")} />
    ),
    verify: () => <ConfirmEmailStep {...shared} onStartOver={toStart} />,
    about: () => <AboutYouStep intent={props.intent} onDone={steps.finishAbout} />,
  };
  return bodies[steps.step]();
}

export function AuthDialog(props: AuthDialogProps) {
  const steps = useAuthSteps(props);
  const description = steps.step === "start" ? props.reason : undefined;
  const title = steps.step === "start" && steps.purpose ? START_TITLES[steps.purpose] : TITLES[steps.step];
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        <StepBody {...props} steps={steps} />
      </DialogContent>
    </Dialog>
  );
}
